import React, { useEffect, useState } from 'react';
import './SegmentCounts.css';
import RevealMore from '../RevealMore/RevealMore';
import { activateSegmentPlay, fetchPlays, pausePlay, archivePlay, PlaysApiError } from '../Plays/playsApi';

// Traduce los codigos de error del contrato del endpoint a un mensaje humano.
function activationErrorMessage(err) {
  const code = err instanceof PlaysApiError ? err.code : null;
  const status = err instanceof PlaysApiError ? err.status : null;
  if (code === 'free_activation_blocked' || status === 403) {
    return 'Upgrade your plan to activate this play.';
  }
  if (status === 402) {
    return "You're out of plays. Upgrade to activate more.";
  }
  if (code === 'delivery_provider_ambiguous') {
    return 'More than one email platform is connected. Tell us which one to use.';
  }
  if (code === 'delivery_provider_not_ready' || code === 'provider_not_ready' || status === 409) {
    return 'Connect your email platform first.';
  }
  if (code === 'segment_not_activatable' || status === 400) {
    return "This segment can't be activated yet.";
  }
  return "Couldn't activate. Try again.";
}

// Base del dashboard API (mismo host que usa RevenueFinder).
const DASHBOARD_API_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

// Billing gating: la vista free defaultea a BLOQUEADO (candado). El CTA
// "Activate as Play" habilitado SOLO aparece si billing esta realmente
// encendido (flag ON) Y el backend confirma capacidad (canActivate real).
// Con el flag apagado (estado de hoy en prod, useBillingQuota inerte
// devuelve canActivate:true) el default seguro es candado, no habilitado.
const BILLING_UI_ENABLED = process.env.REACT_APP_ENABLE_BILLING === 'true';

// Los 4 segmentos principales que siempre van visibles arriba del pliegue.
// El resto (Lost, New, New - Lost + cualquier desconocido) va al desplegable
// de "Recovery & onboarding".
const ALWAYS_ON_DEFAULT_SEGMENTS = new Set([
  'VIP',
  'Potential VIP',
  'At Risk',
  'New - High Potential',
]);

// Modo default de activacion por segmento: los segmentos "vivos" (VIP,
// Potential VIP, New - High Potential) arrancan en always-on porque son
// relaciones que hay que sostener; los de recuperacion (At Risk, Lost,
// New - Lost) arrancan en send-once porque son una campaña puntual de rescate.
const DEFAULT_MODE_BY_SEGMENT = {
  'VIP': 'keep_updated',
  'Potential VIP': 'keep_updated',
  'New - High Potential': 'keep_updated',
  'New': 'keep_updated',
  'At Risk': 'launch_once',
  'Lost': 'launch_once',
  'New - Lost': 'launch_once',
};

// Copys por segmento (los 7 predictivos). Los accionables como Play
// (spec decisión B) son VIP, Potential VIP, At Risk, New - High Potential;
// el backend ya marca `actionable`. Aquí solo damos subtítulo humano.
const SEGMENT_COPY = {
  'VIP': 'Your best customers. High value, still alive.',
  'Potential VIP': 'One step away from VIP. Nudge them up.',
  'At Risk': 'Loyal buyers going quiet. Win them back now.',
  'New - High Potential': 'Fresh buyers that look like future VIPs.',
  'New': 'Recently acquired. Still forming a habit.',
  'Lost': 'Gone quiet a while ago — worth one win-back shot.',
  'New - Lost': "Bought once, never came back — one nudge before you let go.",
};

// Tag de intencion por segmento (pastilla de tinte suave). Los "desconocidos"
// (no listados) caen en Onboard como default neutro.
const INTENT_TAG_BY_SEGMENT = {
  'VIP': { label: 'Protect', kind: 'grow' },
  'Potential VIP': { label: 'Grow', kind: 'grow' },
  'New - High Potential': { label: 'Grow', kind: 'grow' },
  'At Risk': { label: 'Win back', kind: 'winback' },
  'Lost': { label: 'Win back', kind: 'winback' },
  'New - Lost': { label: 'Win back', kind: 'winback' },
  'New': { label: 'Onboard', kind: 'onboard' },
};

function IntentTag({ segment }) {
  const tag = INTENT_TAG_BY_SEGMENT[segment] || { label: 'Onboard', kind: 'onboard' };
  return <span className={`sc-tag sc-tag--${tag.kind}`}>{tag.label}</span>;
}

/**
 * Vista free: conteo de clientes POR SEGMENTO individual con datos REALES.
 * Es la carnada de conversión — el cliente VE el valor, pero para ACCIONARLO
 * (empujar a Klaviyo) tiene que pagar. Aquí NO se activa nada.
 *
 * Props:
 *  - companyId: string (company logueado; el aislamiento real es server-side)
 *  - token: Bearer del dashboard API
 *  - canActivate: bool — true si el plan permite activar (pago). Default false.
 *  - onUpgrade: () => void — callback del CTA de upgrade (opcional).
 */
// Tarjeta accionable con su propio estado de activacion (aislado por segmento).
// Solo se renderiza cuando activationUnlocked === true. El path de candado
// (free) vive en el componente padre y no pasa por aqui.
//
// livePlay: el billing play real (de /plays) que hace match por nombre con
// este segmento (ver matchPlayForSegment en el padre). Cuando existe y esta
// active/paused, mostramos Pause/Archive reales en vez de/ademas del estado
// local "done" -- misma logica y llamadas que MyPlays.jsx (pausePlay/
// archivePlay), no se duplica ninguna regla de negocio, solo se reusan.
function ActionableCard({ segment, token, onUpgrade, controlTabIndex, livePlay, onPlaysChanged }) {
  const [mode, setMode] = useState(DEFAULT_MODE_BY_SEGMENT[segment] || 'keep_updated');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const isUpgrade = (err) =>
    err instanceof PlaysApiError && (err.code === 'free_activation_blocked' || err.status === 403 || err.status === 402);

  const handleActivate = () => {
    if (status === 'loading' || status === 'done') return;
    setStatus('loading');
    setErrMsg(null);
    activateSegmentPlay({ token, segment, mode })
      .then(() => {
        setStatus('done');
        if (typeof onPlaysChanged === 'function') onPlaysChanged();
      })
      .catch((err) => {
        setStatus('error');
        setErrMsg(activationErrorMessage(err));
        if (isUpgrade(err) && typeof onUpgrade === 'function') onUpgrade();
      });
  };

  const handlePause = () => {
    if (!livePlay || busy) return;
    setBusy(true);
    setErrMsg(null);
    pausePlay({ token, playId: livePlay.id })
      .then(() => {
        if (typeof onPlaysChanged === 'function') onPlaysChanged();
      })
      .catch((err) => setErrMsg(err?.message || 'Could not pause this Play.'))
      .finally(() => setBusy(false));
  };

  const handleArchive = () => {
    if (!livePlay || busy) return;
    if (!window.confirm('Archive this play? It will stop counting toward your results.')) return;
    setBusy(true);
    setErrMsg(null);
    archivePlay({ token, playId: livePlay.id })
      .then(() => {
        if (typeof onPlaysChanged === 'function') onPlaysChanged();
      })
      .catch((err) => setErrMsg(err?.message || 'Could not archive this Play.'))
      .finally(() => setBusy(false));
  };

  // Hay un play real (de billing) para este segmento, activo o pausado:
  // mostramos controles reales en vez del boton "Activated" local, que no
  // sabia nada de billing y no se podia deshacer.
  if (livePlay && (livePlay.status === 'active' || livePlay.status === 'paused')) {
    return (
      <>
        <div className="sc-live-actions">
          {livePlay.status === 'active' && (
            <button
              className="sc-cta sc-cta--pause"
              type="button"
              onClick={handlePause}
              disabled={busy}
              tabIndex={controlTabIndex}
            >
              {busy ? 'Working…' : 'Pause'}
            </button>
          )}
          <button
            className="sc-cta sc-cta--danger"
            type="button"
            onClick={handleArchive}
            disabled={busy}
            tabIndex={controlTabIndex}
          >
            {busy ? 'Working…' : 'Archive'}
          </button>
        </div>
        {errMsg && <p className="sc-cta-error">{errMsg}</p>}
      </>
    );
  }

  if (status === 'done') {
    return (
      <button className="sc-cta sc-cta--done" type="button" disabled tabIndex={controlTabIndex}>
        <span className="material-symbols-outlined sc-lock-icon">check_circle</span>
        Activated
      </button>
    );
  }

  return (
    <>
      <div className="sc-mode-toggle" role="group" aria-label="Activation mode">
        <button
          type="button"
          className={`sc-mode-opt${mode === 'keep_updated' ? ' sc-mode-opt--on' : ''}`}
          onClick={() => setMode('keep_updated')}
          disabled={status === 'loading'}
          tabIndex={controlTabIndex}
        >
          Always-on
        </button>
        <button
          type="button"
          className={`sc-mode-opt${mode === 'launch_once' ? ' sc-mode-opt--on' : ''}`}
          onClick={() => setMode('launch_once')}
          disabled={status === 'loading'}
          tabIndex={controlTabIndex}
        >
          Send once
        </button>
      </div>
      <button
        className="sc-cta sc-cta--active"
        type="button"
        onClick={handleActivate}
        disabled={status === 'loading'}
        tabIndex={controlTabIndex}
      >
        {status === 'loading' ? 'Activating…' : 'Activate as Play'}
      </button>
      {status === 'error' && errMsg && <p className="sc-cta-error">{errMsg}</p>}
    </>
  );
}

// Fila compacta de lista: una por segmento. La fila muestra nombre + tag,
// valor total / por cliente (con los mismos guards anti-NaN de siempre:
// solo si value y count son > 0) y el conteo en morado. El bloque de
// activacion EXISTENTE (ActionableCard con toggle de modo, o el candado de
// upgrade) no cabe elegante en una fila, asi que la fila se expande al click
// y lo muestra debajo — misma logica, solo cambia la presentacion.
function SegmentRow({ s, activationUnlocked, token, onUpgrade, ariaHidden, tabIndexOverride, livePlay, onPlaysChanged }) {
  const [open, setOpen] = useState(false);
  const detailId = `sc-row-detail-${String(s.segment).replace(/\s+/g, '-').toLowerCase()}`;
  const count = Number(s.count) || 0;
  const value = Number(s.value) || 0;
  const perCustomer = value > 0 && count > 0 ? Math.round(value / count) : null;

  return (
    <div
      className="sc-row-wrap"
      aria-hidden={ariaHidden || undefined}
      inert={ariaHidden ? true : undefined}
    >
      <button
        type="button"
        className={`sc-row${open ? ' sc-row--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
        tabIndex={tabIndexOverride}
      >
        <span className="sc-row-name-wrap">
          <span className="sc-row-name">{s.segment}</span>
          <IntentTag segment={s.segment} />
        </span>

        <span className="sc-row-value">
          {perCustomer !== null && (
            <>
              <span className="sc-row-value-total">${value.toLocaleString()}</span>
              <span className="sc-row-value-per">${perCustomer.toLocaleString()} / customer</span>
            </>
          )}
        </span>

        <span className="sc-row-count-wrap">
          <span className="sc-row-count">{count.toLocaleString()}</span>
          <span className="sc-row-count-label">customers</span>
        </span>

        <span className="sc-row-control">
          {activationUnlocked && livePlay && (livePlay.status === 'active' || livePlay.status === 'paused') && (
            <span className={`sc-row-live-pill sc-row-live-pill--${livePlay.status}`}>
              {livePlay.status === 'active' ? 'Active' : 'Paused'}
            </span>
          )}
          {!activationUnlocked && (
            <span
              className="material-symbols-outlined sc-row-lock"
              title="Activation is available on paid plans"
            >
              lock
            </span>
          )}
          <span className={`sc-row-chevron${open ? ' sc-row-chevron--open' : ''}`} aria-hidden="true">
            ▾
          </span>
        </span>
      </button>

      {open && (
        <div id={detailId} className="sc-row-detail">
          <p className="sc-card-copy sc-row-copy">{SEGMENT_COPY[s.segment] || ''}</p>
          {/* Decision de Alejo: los 7 segmentos son activables en ambos modos.
              Sin gating por s.actionable — todas las filas (incl. recuperacion
              en el desplegable) llevan su ActionableCard (unlocked) o candado. */}
          {activationUnlocked ? (
            <ActionableCard
              segment={s.segment}
              token={token}
              onUpgrade={onUpgrade}
              controlTabIndex={tabIndexOverride}
              livePlay={livePlay}
              onPlaysChanged={onPlaysChanged}
            />
          ) : (
            <button
              className="sc-cta sc-cta--locked"
              type="button"
              onClick={onUpgrade}
              title="Activation is available on paid plans"
              tabIndex={tabIndexOverride}
            >
              <span className="material-symbols-outlined sc-lock-icon">lock</span>
              Upgrade to activate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function SegmentCounts({ companyId, token, canActivate = false, onUpgrade }) {
  // Default seguro = bloqueado. Solo habilitado si billing esta encendido
  // Y el backend confirma capacidad real de activar.
  const activationUnlocked = BILLING_UI_ENABLED && canActivate === true;
  const [segments, setSegments] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Plays reales de billing (para saber si un segmento ya tiene un play
  // activo/pausado y mostrar Pause/Archive en vez del estado local "done").
  // Mismo endpoint que usa MyPlays.jsx. Fail-open: si billing no responde,
  // simplemente no hay livePlays y la pantalla se comporta como hoy (el
  // estado local "done" sigue funcionando sin romper nada).
  const [billingPlays, setBillingPlays] = useState([]);
  const [playsRefreshKey, setPlaysRefreshKey] = useState(0);

  useEffect(() => {
    if (!activationUnlocked || !token) {
      setBillingPlays([]);
      return;
    }
    let cancelled = false;
    fetchPlays({ token })
      .then((data) => {
        if (!cancelled) setBillingPlays(Array.isArray(data?.plays) ? data.plays : []);
      })
      .catch(() => {
        // Fail gracefully: sin plays reales, la pantalla degrada al
        // comportamiento actual (estado local "done" por segmento).
        if (!cancelled) setBillingPlays([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activationUnlocked, token, playsRefreshKey]);

  const refreshPlays = () => setPlaysRefreshKey((k) => k + 1);

  // Mismo criterio de matching que MyPlays.jsx (join por nombre,
  // case-insensitive) ya que aca no hay un id de segmento comun con billing.
  // Si hay varios plays con el mismo nombre, preferimos el activo, luego el
  // mas recientemente activado -- igual que el padre de MyPlays.
  const matchPlayForSegment = (segmentName) => {
    const key = (segmentName || '').trim().toLowerCase();
    if (!key) return null;
    const candidates = billingPlays.filter((bp) => (bp.name || '').trim().toLowerCase() === key);
    if (candidates.length === 0) return null;
    const ranked = [...candidates].sort((a, b) => {
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (b.status === 'active' && a.status !== 'active') return 1;
      const aDate = a.activated_at ? new Date(a.activated_at).getTime() : 0;
      const bDate = b.activated_at ? new Date(b.activated_at).getTime() : 0;
      return bDate - aDate;
    });
    return ranked[0];
  };

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(
      `${DASHBOARD_API_URL}/api/dashboard/segment-counts?company=${encodeURIComponent(companyId)}`,
      { headers }
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setSegments(Array.isArray(data?.segments) ? data.segments : []);
        setTotal(data?.totalCustomers || 0);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, token]);

  if (loading) {
    return (
      <section className="sc-wrapper">
        <div className="sc-header">
          <h2 className="sc-title">Your customer segments</h2>
        </div>
        <div className="sc-skeleton-list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="sc-row-skeleton" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="sc-wrapper">
        <div className="sc-header">
          <h2 className="sc-title">Your customer segments</h2>
        </div>
        <p className="sc-empty">Couldn't load your segments right now.</p>
      </section>
    );
  }

  if (!segments || segments.length === 0 || total === 0) {
    return (
      <section className="sc-wrapper">
        <div className="sc-header">
          <h2 className="sc-title">Your customer segments</h2>
        </div>
        <p className="sc-empty">
          We're still building your customer segments. Check back once your first
          data sync completes.
        </p>
      </section>
    );
  }

  const mainSegments = segments.filter((s) => ALWAYS_ON_DEFAULT_SEGMENTS.has(s.segment));
  const recoverySegments = segments.filter((s) => !ALWAYS_ON_DEFAULT_SEGMENTS.has(s.segment));

  // Titulo rico del disparador "ver mas": total de clientes del grupo, y si
  // el backend manda `value` (USD) en todos/algunos segmentos, tambien el
  // monto agregado. Retrocompatible: sin `value` en el payload, solo cuenta.
  const recoveryCustomerTotal = recoverySegments.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0
  );
  const recoveryValueTotal = recoverySegments.reduce(
    (sum, s) => sum + (Number(s.value) || 0),
    0
  );
  const recoveryRevealLabel = (
    <>
      <span className="reveal-more-num">{recoverySegments.length}</span> more segments ·{' '}
      <span className="reveal-more-num">{recoveryCustomerTotal.toLocaleString()}</span> customers
      {recoveryValueTotal > 0 ? (
        <>
          {' '}· <span className="reveal-more-num">${recoveryValueTotal.toLocaleString()}</span> in value
        </>
      ) : null}
    </>
  );

  return (
    <section className="sc-wrapper">
      <div className="sc-header">
        <h2 className="sc-title">Your customer segments</h2>
        <p className="sc-subtitle">
          {total.toLocaleString()} customers, grouped by what they need next.
          {!activationUnlocked && ' Upgrade to push any of these to your email tool.'}
        </p>
      </div>

      {mainSegments.length > 0 && (
        <>
          <p className="sc-kicker">Act on these first</p>
          <div className="sc-list">
            {mainSegments.map((s) => (
              <SegmentRow
                key={s.segment}
                s={s}
                activationUnlocked={activationUnlocked}
                token={token}
                onUpgrade={onUpgrade}
                livePlay={matchPlayForSegment(s.segment)}
                onPlaysChanged={refreshPlays}
              />
            ))}
          </div>
        </>
      )}

      {recoverySegments.length > 0 && (
        <div className="sc-recovery">
          <p className="sc-kicker sc-recovery-kicker">Recovery & onboarding</p>
          <RevealMore
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
            label={recoveryRevealLabel}
            panelId="sc-recovery-panel"
          >
            <div className="sc-list">
              {recoverySegments.map((s) => (
                <SegmentRow
                  key={s.segment}
                  s={s}
                  activationUnlocked={activationUnlocked}
                  token={token}
                  onUpgrade={onUpgrade}
                  ariaHidden={!expanded}
                  tabIndexOverride={!expanded ? -1 : undefined}
                  livePlay={matchPlayForSegment(s.segment)}
                  onPlaysChanged={refreshPlays}
                />
              ))}
            </div>
          </RevealMore>
        </div>
      )}
    </section>
  );
}
